import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';

import { KeyValue } from '#components/shared/KeyValue.tsx';
import { TimeTooltip } from '#components/shared/TimeTooltip.tsx';
import { hasStopDeviation } from '#domain/model/hasDeviation.ts';
import type { ServiceOrder } from '#domain/model/ServiceOrder.ts';
import type { Stop } from '#domain/model/Stop.ts';
import type { TransportOrder } from '#domain/model/TransportOrder.ts';
import { TimeDifference } from '#utils/timeDifference.tsx';

type TourStopItemProps = {
    stop: Stop;
    isActiveStop: boolean;
    transportOrders: TransportOrder[];
    serviceOrders: ServiceOrder[];
    index: number;
    setSelectedStopId: (stop?: string) => void;
};

export const TourStopItem = ({
    stop,
    isActiveStop,
    transportOrders,
    serviceOrders,
    index,
    setSelectedStopId,
}: TourStopItemProps) => {
    const intl = useIntl();
    const isFinished = stop.status === 'finished';
    return (
        <div className='timeline-element padding-left-5 padding-y-10 margin-bottom-10'>
            <div className='timeline-date margin-right-10'>
                <div className={getStopTimelineBadgeClassName(isFinished, isActiveStop)}>{index + 1}</div>
            </div>
            <div className='timeline-content width-100pct'>
                <div className='text-medium margin-bottom-5'>{stop.name}</div>
                <KeyValue
                    keyData={intl.formatMessage({ id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.activity' })}
                    valueData={
                        <FormattedMessage id={`intl-msg:ordercom-cloud-frontend.tours.stop.type.${stop.type}`} />
                    }
                />
                <KeyValue
                    keyData={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.address',
                    })}
                    valueData={`${stop.location.country} - ${stop.location.zipCode} ${stop.location.city}`}
                />
                <KeyValue keyData={''} valueData={stop.location.street} />
                <KeyValue
                    keyData={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.arrivalTime',
                    })}
                    valueData={
                        <TimeTooltip value={stop.startTarget}>
                            <div className='flex-1-1-0 text-medium'>
                                <FormattedDate
                                    value={stop.startActual ?? stop.startTarget}
                                    timeStyle={'short'}
                                    dateStyle={'short'}
                                />
                                <TimeDifference actual={stop.startActual} expected={stop.startTarget} />
                            </div>
                        </TimeTooltip>
                    }
                />
                <KeyValue
                    keyData={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.departureTime',
                    })}
                    valueData={
                        <TimeTooltip value={stop.endTarget}>
                            <div className='flex-1-1-0 text-medium'>
                                <FormattedDate
                                    value={stop.endActual ?? stop.endTarget}
                                    timeStyle={'short'}
                                    dateStyle={'short'}
                                />
                                <TimeDifference actual={stop.endActual} expected={stop.endTarget} />
                            </div>
                        </TimeTooltip>
                    }
                />

                {transportOrders.length !== 0 && (
                    <>
                        <KeyValue
                            keyData={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.transportOrders',
                            })}
                            valueData={
                                <span>
                                    {intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.transportOrders.value',
                                    })}
                                </span>
                            }
                            valueDataAddon={
                                hasStopDeviation(stop.type, transportOrders) && (
                                    <span className='text-color-danger text-size-10 margin-left-10'>
                                        {intl.formatMessage({
                                            id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.details.value',
                                        })}
                                    </span>
                                )
                            }
                            isLink
                            onClick={() => setSelectedStopId(stop.id)}
                        />
                        {transportOrders.map(order => (
                            <KeyValue keyData={''} key={order.id} valueData={order.secondary_reference_id} />
                        ))}
                    </>
                )}

                {serviceOrders.length !== 0 && (
                    <>
                        <KeyValue
                            keyData={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.serviceOrders',
                            })}
                            valueData={
                                <span>
                                    {intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.serviceOrders.value',
                                    })}
                                </span>
                            }
                            isLink
                            onClick={() => setSelectedStopId(stop.id)}
                        />
                        {serviceOrders.map(order => (
                            <KeyValue keyData={''} key={order.id} valueData={order.secondary_reference_id} />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

const getStopTimelineBadgeClassName = (isFinished: boolean, isActive: boolean) => {
    const baseClassName = 'text-medium badge';
    if (isFinished) {
        return `${baseClassName} badge-success text-color-white`;
    }
    if (isActive) {
        return `${baseClassName} badge-info text-color-white scale-130`;
    }
    return `${baseClassName} bg-white border border-color-info text-color-info`;
};
